import { collection, getDocs, doc, getDoc, addDoc, updateDoc, query, where, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { Office } from '../types';

// Collection names matching mobile app
const OFFICES_REF = 'Offices';

// Check if Firebase is properly initialized
const isFirebaseAvailable = () => {
  try {
    return db && typeof db === 'object';
  } catch (error) {
    console.error('Firebase not available:', error);
    return false;
  }
};

// Get all offices
export const getAllOffices = async (): Promise<Office[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officesRef = collection(db, OFFICES_REF);
    const querySnapshot = await getDocs(officesRef);
    
    const offices: Office[] = [];
    querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
      const officeData = doc.data() as Office;
      offices.push({
        ...officeData,
        id: doc.id
      });
    });
    
    return offices;
  } catch (error) {
    console.error('Error fetching offices:', error);
    throw error;
  }
};

// Get office by ID
export const getOfficeById = async (officeId: string): Promise<Office | null> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    const officeSnap = await getDoc(officeRef);
    
    if (officeSnap.exists()) {
      const officeData = officeSnap.data() as Office;
      return {
        ...officeData,
        id: officeSnap.id
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching office:', error);
    throw error;
  }
};

// Get offices by creator ID
export const getOfficesByCreator = async (creatorId: string): Promise<Office[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officesRef = collection(db, OFFICES_REF);
    const q = query(officesRef, where('creatorID', '==', creatorId));
    const querySnapshot = await getDocs(q);
    
    const offices: Office[] = [];
    querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
      const officeData = doc.data() as Office;
      offices.push({
        ...officeData,
        id: doc.id
      });
    });
    
    return offices;
  } catch (error) {
    console.error('Error fetching offices by creator:', error);
    throw error;
  }
};

// Get active offices (not archived or deleted)
export const getActiveOffices = async (): Promise<Office[]> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officesRef = collection(db, OFFICES_REF);
    const q = query(
      officesRef,
      where('isArchived', '==', false),
      where('isDeleted', '==', false)
    );
    
    const querySnapshot = await getDocs(q);
    
    const offices: Office[] = [];
    querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
      const officeData = doc.data() as Office;
      offices.push({
        ...officeData,
        id: doc.id
      });
    });
    
    return offices;
  } catch (error) {
    console.error('Error fetching active offices:', error);
    throw error;
  }
};

// Add new office
export const addOffice = async (officeData: Omit<Office, 'id'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    console.log('📝 OfficeService: Attempting to add office to Firestore...');
    console.log('📝 OfficeService: Office data:', officeData);
    
    const officesRef = collection(db, OFFICES_REF);
    const docRef = await addDoc(officesRef, officeData);
    
    console.log('✅ OfficeService: Office added successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ OfficeService: Error adding office:', error);
    console.error('❌ OfficeService: Error details:', {
      code: (error as any)?.code,
      message: (error as any)?.message,
      stack: (error as any)?.stack
    });
    throw error;
  }
};

// Update office
export const updateOffice = async (officeId: string, officeData: Partial<Office>): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    await updateDoc(officeRef, officeData);
  } catch (error) {
    console.error('Error updating office:', error);
    throw error;
  }
};

// Delete office (soft delete by setting isDeleted to true)
export const deleteOffice = async (officeId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    await updateDoc(officeRef, {
      isDeleted: true,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error deleting office:', error);
    throw error;
  }
};

// Archive office
export const archiveOffice = async (officeId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    await updateDoc(officeRef, {
      isArchived: true,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error archiving office:', error);
    throw error;
  }
};

// Unarchive office
export const unarchiveOffice = async (officeId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    await updateDoc(officeRef, {
      isArchived: false,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error unarchiving office:', error);
    throw error;
  }
};

// Activate office
export const activateOffice = async (officeId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    await updateDoc(officeRef, {
      isActive: true,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error activating office:', error);
    throw error;
  }
};

// Inactivate office
export const inactivateOffice = async (officeId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officeRef = doc(db, OFFICES_REF, officeId);
    await updateDoc(officeRef, {
      isActive: false,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error inactivating office:', error);
    throw error;
  }
};

// Get offices by lawyer ID (lawyer is in stuffMembersList)
export const fetchOfficesByLawyerID = async (lawyerId: string): Promise<Office[]> => {
  try {
    console.log('🔍 Debug - fetchOfficesByLawyerID called with lawyerId:', lawyerId);
    
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }

    const officesRef = collection(db, OFFICES_REF);
    const q = query(officesRef, where('stuffMembersList', 'array-contains', lawyerId));
    console.log('🔍 Debug - Executing Firestore query for offices');
    const querySnapshot = await getDocs(q);
    console.log('🔍 Debug - Firestore query completed, docs count:', querySnapshot.size);
    
    const offices: Office[] = [];
    querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
      const officeData = doc.data() as Office;
      console.log('🔍 Debug - Processing office doc:', doc.id, officeData.name);
      offices.push({
        ...officeData,
        id: doc.id
      });
    });
    
    console.log('✅ Debug - fetchOfficesByLawyerID completed, returning', offices.length, 'offices');
    return offices;
  } catch (error) {
    console.error('❌ Debug - Error fetching offices by lawyer ID:', error);
    throw error;
  }
}; 